import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';
import { IconButton } from './Button.js';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger';

const GLYPH: Record<BannerTone, IconName> = { info: 'info', success: 'check', warning: 'alert-triangle', danger: 'alert-circle' };

export interface BannerProps {
  tone: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Optional actions, rendered after the text. */
  actions?: ReactNode;
  /** Non-critical banners may be dismissed (03-components §6.2). */
  onDismiss?: () => void;
  className?: string;
  /** Errors are alerts; the rest are status. */
  role?: 'status' | 'alert' | 'none';
  icon?: IconName;
}

/** Persistent, contextual notice placed directly above the region it concerns (03-components §6.2). */
export function Banner({ tone, title, children, actions, onDismiss, className, role, icon }: BannerProps) {
  const liveRole = role ?? (tone === 'danger' ? 'alert' : 'status');
  return (
    <div className={['banner', `banner--${tone}`, className].filter(Boolean).join(' ')} role={liveRole === 'none' ? undefined : liveRole}>
      <Icon name={icon ?? GLYPH[tone]} size={18} className="banner__icon" />
      <div className="banner__body">
        {title ? <p className="banner__title">{title}</p> : null}
        {children ? <div className="banner__text">{children}</div> : null}
        {actions ? <div className="banner__actions cluster">{actions}</div> : null}
      </div>
      {onDismiss ? <IconButton icon="x" label="Dismiss" size="sm" onClick={onDismiss} className="banner__dismiss" /> : null}
    </div>
  );
}
